import { Store } from '@ngrx/store';
import type { GraphQLFormattedError } from 'graphql';

import { ConsentType, ShowFeedbackError } from '@resident-nx/shared';

/**
 * Backend errors that already have dedicated handling, so a toast would either
 * duplicate an existing message or fire while the user is being redirected out
 * of the flow anyway.
 */
const HANDLED_ELSEWHERE = [
  // token-refresh.interceptor retries these
  'UNAUTHENTICATED',
  'FORBIDDEN',
  // Requests that raced an unauthenticated navigation (guards of a route run
  // concurrently, so a data guard can fire before AuthWebGuard redirects).
  // The user is on their way to the login page — a toast would only confuse.
  'Token validation failed',
  // consent.interceptor / missingConsentInterceptor route into the consent flow
  'MANDATORY_CONSENT_FOR_USER_IS_MISSING',
  ...Object.values(ConsentType),
];

/**
 * Backend error codes with a dedicated message. Translation keys are used
 * without their `resident_*` prefix, which the translation loader strips.
 */
const ERROR_MESSAGE_KEYS: Record<string, string> = {
  NOT_ALLOWED_DURING_IMPERSONATION: 'impersonation.not_allowed_error_l',
};

const GENERIC_ERROR_MESSAGE_KEY = 'general.error';

/**
 * Every field a backend error code can hide in. graphql-java puts its own value
 * in `extensions.classification` (e.g. "DataFetchingException") and the actual
 * code in the message, so all of them are searched rather than the first
 * non-empty one.
 */
const getErrorHaystacks = (errors: readonly GraphQLFormattedError[]): string[] =>
  errors.flatMap(error => {
    const extensions = error.extensions as { code?: string; classification?: string } | undefined;
    return [extensions?.code, extensions?.classification, error.message].filter(
      (value): value is string => !!value
    );
  });

/**
 * Shows an error toast for backend errors, so individual callers do not have to
 * handle them. Wired into the Apollo error link from web-saas only — see
 * createDefaultApolloConnection.
 */
export function showBackendErrorToast(
  store: Store,
  errors: readonly GraphQLFormattedError[]
): void {
  if (!errors?.length) {
    return;
  }

  const haystacks = getErrorHaystacks(errors);
  const contains = (needle: string) => haystacks.some(haystack => haystack.includes(needle));

  if (HANDLED_ELSEWHERE.some(contains)) {
    return;
  }

  // One toast per response, even when the backend reports several errors.
  const message =
    Object.entries(ERROR_MESSAGE_KEYS).find(([code]) => contains(code))?.[1] ??
    GENERIC_ERROR_MESSAGE_KEY;

  store.dispatch(ShowFeedbackError({ message }));
}
